import React, { SFC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogTitle, makeStyles, DialogContent, DialogActions, Grid, MenuItem, Typography, Checkbox, Tooltip, IconButton } from '@material-ui/core'
import { Form } from 'react-final-form'
import { Select, TextField } from 'mui-rff'
import { TagModel, TagType } from '../../../shared/types/tag'
import { ValidationErrors } from 'final-form'
import { useDispatch, useSelector } from 'react-redux'
import { addTag, editTag } from '../../../redux/actions/tags'
import { setRequiredErrors } from '../../../utils/ui'
import { DialogConfirmButton } from '../../../shared/components/dialogConfirmButton'
import { TagTypeSelect } from '../../../shared/components/tagTypeSelect'
import { config } from '../../../config'
import { DialogProgress } from '../../../shared/components/dialogProgress'
import { Tenant } from '../../../shared/types/tenant'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'hidden',
    },
    checkbox: {
        color: '#6100ED', // purple when enabled (unchecked)
        '&$checked': {
            color: '#6100ED', // purple when enabled (checked)
        },
        '&$disabled': {
            color: '#A0A0A0', // grey when disabled (unchecked)
        },
        '&$checked$disabled': {
            color: '#A0A0A0', // grey when disabled (checked)
        },
        marginBottom: '4px',
        padding: '0 8px 0 0',
    },
    checked: {},   // required for overrides
    disabled: {},  // required for overrides
}))

interface TagAddEditDialogProps {
    open: boolean
    isAdd: boolean
    onClose(tag?: TagModel): void
    initialModel?: TagModel
}
const TagAddEditDialog: SFC<TagAddEditDialogProps> = ({ open, isAdd, onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const tenantsState = useSelector((state: any) => state.tenants)
    const [isExclusive, setIsExclusive] = useState<boolean>(initialModel?.isExclusive || false)

    const stateSelector = isAdd ? (state: any) => state.tags.add : (state: any) => state.tags.edit

    const handleIsExclusiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsExclusive(event.target.checked);
    };

    useEffect(() => {
        if (initialModel && initialModel.isExclusive !== undefined) {
            setIsExclusive(initialModel.isExclusive);
        }
    }, [initialModel]);

    return (
        <Dialog className={classes.dialog} open={open}>
            <DialogProgress stateSelector={stateSelector} />
            <DialogTitle>{isAdd ? t('tags.newTag') : t('tags.editTag')}</DialogTitle>

            <Form
                onSubmit={async (model) => {
                    model = {
                        ...model,
                        tenantId: model.tenantId ? model.tenantId : tenantId,
                        isExclusive: model.type === TagType.User ? isExclusive : false,
                    }
                    const result: any = await dispatch(isAdd ? addTag(model as TagModel) : editTag(model as TagModel))
                    if (result) {
                        onClose(result)
                    }
                }}
                initialValues={initialModel}
                validate={(model: any): ValidationErrors => {
                    const result: any = {}

                    let requiredFields = ['name', 'type']
                    if (tenantId && tenantId === config.mainTenantId) {
                        requiredFields.push('tenantId')
                    }
                    setRequiredErrors(result, model, requiredFields, t('common.required'))
                    return result
                }}
                render={({ handleSubmit, form, values }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form}>
                        <DialogContent>
                            <Grid container alignItems="flex-start" spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        name="name"
                                        fullWidth
                                        inputProps={{
                                            maxLength: 128,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('common.name')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TagTypeSelect
                                        name="type"
                                        required
                                        label={t('tags.type')}
                                        variant="filled"
                                    ></TagTypeSelect>
                                </Grid>
                                <Grid hidden={!(tenantId && tenantId === config.mainTenantId)} item xs={12}>
                                    <Select
                                        required
                                        name="tenantId"
                                        variant="filled"
                                        label={t('common.tenant')}
                                    >
                                        {tenantsState.value
                                            ?.filter((item: Tenant) => item.id !== config.mainTenantId)
                                            .map((item: Tenant) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        name="description"
                                        fullWidth
                                        inputProps={{
                                            maxLength: 128,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('common.description')}
                                    />
                                </Grid>
                                {Number(values?.type) === TagType.User && tenantId === config.mainTenantId && (<Grid item xs={12}>
                                    <Typography variant="caption" style={{ fontSize: '18px', color: '#000000' }}>
                                        <Checkbox
                                            name="isExclusive"
                                            checked={isExclusive}
                                            onChange={handleIsExclusiveChange}
                                            classes={{
                                                root: classes.checkbox,
                                                checked: classes.checked,
                                                disabled: classes.disabled,
                                            }}
                                        />
                                        {t('tags.isExclusive')}
                                        <Tooltip title='Used in specific cases like charging via terminal. It means that all other tags (including charger/location tags) are ignored and pricing condition with this tag is selected only. Also, users with this tag does not need to have assigned Debet Card. Invoice wont be created and price will be charged in separate way.' arrow>
                                            <IconButton size="small" style={{ marginLeft: 2, bottom: 1 }}>
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button
                                onClick={() => {
                                    onClose()
                                }}
                                color="primary"
                            >
                                {t('common.cancel')}
                            </Button>
                            <DialogConfirmButton
                                label={isAdd ? t('common.create') : t('common.change')}
                                stateSelector={stateSelector}
                            />
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

export default TagAddEditDialog
